import { useRouter } from '@src/routes'
import { TabsEnum } from '@src/routes/Configurators/products/TubesMTORedesign/ProductsConfiguration'
import { FormattedMessage } from 'react-intl'
import { SemiConfigProductTypes, ActiveLinkType } from './enum'

interface SearchDataProps {
  searchKey: string
  page: number
  count: string
  facet: string[]
  sort: string
  order: string
  searchErrorMessage: string
}

export interface SemiConfigClone {
  geneId: string
  geneSymbol: string
  species: string
  price: string | null
  currency: string | null
  moreInformation: string
  efficiencyScore?: string
  specificityScore?: string
  grnaId?: string
  recommendedStatus?: string
}

interface SemiConfigSpecifications {
  physicalMaterial: string
  amount?: string
  titer?: string
  volume?: string
  aliquot?: string
  modification?: string
  structure?: string
  species?: string
  scale?: string
  purification?: string
  format?: string
  lentivirusBackbone?: string
}

// Type of values of the outer formik form
export interface SemiConfigFormValues {
  searchData: SearchDataProps
  displayPageError: boolean
  loading?: boolean
  completedStep: number
  selectedClones: SemiConfigClone[]
  specifications: SemiConfigSpecifications
  activeSearchTab: TabsEnum
}

export interface ResultItem {
  efficiencyScore: string
  geneId: string
  geneSymbol: string
  grnaId: string
  moreInformation: string
  recommendedStatus: string
  species: string
  specificityScore: string
}

export interface ServerErrorType {
  key?: string | null
  value?: string | null
}
export interface SearchResultType {
  controls: ResultItem[]
  reagents: ResultItem[]
  count: number
  serverError?: ServerErrorType[]
  term: string[]
}
export interface specificationItem {
  key: string
  value: string
  enabled?: boolean
}
type SpecificationKeys =
  | 'physicalMaterial'
  | 'amount'
  | 'titer'
  | 'volume'
  | 'aliquot'
  | 'modification'
  | 'structure'
  | 'species'
  | 'scale'
  | 'purification'
  | 'format'
  | 'lentivirusBackbone'
export type SpecificationResultType = {
  [key in SpecificationKeys]: specificationItem[]
}
export const useProductFromQueryParam = (): string => {
  const router = useRouter()
  return router.pathname.substring(router.pathname.lastIndexOf('/') + 1)
}

const {
  SANGERCRISPR,
  ORF,
  PREDESIGNED_SIRNA,
  PREDESIGNED_CRISPR_GRNA,
  PREDESIGNED_SHRNA,
} = SemiConfigProductTypes

export const SemiConfiguratorSearchBoxHeaderTextMap = {
  [PREDESIGNED_CRISPR_GRNA]: {
    id: 'SEARCH_BOX_HEADER_TEXT_PREDESIGNED_CRISPR_GRNA',
    defaultMessage:
      'Search by Gene Symbol, Gene ID or MISSION™ gRNA ID (Enter up to 100 items separated by spaces)',
  },
  [SANGERCRISPR]: {
    id: 'SEARCH_BOX_HEADER_TEXT_SANGERCRISPR',
    defaultMessage:
      'Search by Gene Symbol, Clone ID or Gene ID (enter up to 100 items separated by spaces)',
  },
  [ORF]: {
    id: 'CONF_SEARCH_MESSAGE',
    defaultMessage:
      'Search by Gene Symbol, RefSeq or Gene ID (enter up to 100 items separated by spaces)',
  },

  [PREDESIGNED_SIRNA]: {
    id: 'CONF_SEARCH_MESSAGE',
    defaultMessage:
      'Search by Gene Symbol, RefSeq or Gene ID (enter up to 100 items separated by spaces)',
  },
  [PREDESIGNED_SHRNA]: {
    id: 'CONF_SEARCH_MESSAGE',
    defaultMessage:
      'Search by Gene Symbol, RefSeq or Gene ID (enter up to 100 items separated by spaces)',
  },
}

export const shouldRenderPerformanceGuaranteeLink = (product: string) => {
  const products: string[] = [PREDESIGNED_CRISPR_GRNA]
  return products.includes(product)
}

export const shouldRenderSearchBoxWithoutTabsOnStep1 = (product: string) => {
  const products: string[] = [PREDESIGNED_SIRNA]
  return products.includes(product)
}

export const getSearchBoxHeaderText = (product: string): React.ReactNode => {
  return (
    <FormattedMessage
      id={SemiConfiguratorSearchBoxHeaderTextMap[product]?.id || product}
      defaultMessage={
        SemiConfiguratorSearchBoxHeaderTextMap[product]?.defaultMessage ||
        product
      }
      values={{
        registered: (
          <sup>
            <FormattedMessage
              id={
                SemiConfiguratorSearchBoxHeaderTextMap[product]?.id || product
              }
              defaultMessage={
                SemiConfiguratorSearchBoxHeaderTextMap[product]
                  ?.defaultMessage || product
              }
            />
          </sup>
        ),
      }}
    />
  )
}

const defaultInitialValues = {
  searchData: {
    searchKey: '',
    page: 1,
    count: '10',
    sort: '',
    facet: [],
    order: 'ASC',
    searchErrorMessage: '',
  },
  displayPageError: false,
  loading: false,
  completedStep: 0,
  selectedClones: [],
  specifications: {
    physicalMaterial: '',
  },
  activeSearchTab: TabsEnum.SequenceEntry,
}

export const getInitialValuesBasedOnProduct = (product: string) => {
  switch (product) {
    case ORF:
    case PREDESIGNED_SIRNA:
      return {
        ...defaultInitialValues,
        specifications: {
          ...defaultInitialValues.specifications,
          amount: '',
          titer: '',
        },
      }
    case SANGERCRISPR:
      return {
        ...defaultInitialValues,
        searchData: {
          ...defaultInitialValues.searchData,
          facet: ['species:Human', 'species:Mouse'],
          sort: 'sangerCloneId',
        },
        specifications: {
          ...defaultInitialValues.specifications,
          amount: '',
          titer: '',
        },
      }
    case PREDESIGNED_CRISPR_GRNA:
      return {
        ...defaultInitialValues,
        searchData: {
          ...defaultInitialValues.searchData,
          facet: ['species:Human', 'species:Mouse'],
          sort: 'grnaId',
        },
        specifications: {
          ...defaultInitialValues.specifications,
          amount: '',
          titer: '',
          lentivirusBackbone: '',
          volume: '',
          aliquot: '',
        },
      }
    case PREDESIGNED_SHRNA:
      return {
        ...defaultInitialValues,
        searchData: {
          ...defaultInitialValues.searchData,
          facet: [
            'species:Human',
            'species:Mouse',
            'trc:1',
            'trc:2',
            'validated:Yes',
            'validated:No',
          ],
          sort: 'trcId',
        },
      }
    default:
      return defaultInitialValues
  }
}

export const checkForSemiConfigProduct: (k: string) => boolean = (
  product: string
) => Object.values<string>(SemiConfigProductTypes).includes(product)

//get the active step value from the query string
export const useActiveStepFromQueryParam = (): string => {
  const router = useRouter()
  return Array.isArray(router.query?.activeLink)
    ? router.query?.activeLink[0]
    : router.query?.activeLink || ActiveLinkType.ProductSearch
}

//these files are part of codebase
const multipleUploadTemplate = {
  crisprgrna: 'CRISPR_gRNA_Template.xlsx',
  crispr: 'Sanger_Crispr_Template.xlsx',
  orf: 'ORF_Template.xlsx',
  shrna: 'shRNA_Template.xlsx',
}

export const getProductBasedDownloadUrl = (product: string): string => {
  return multipleUploadTemplate[product]
    ? `/static/configurator/${multipleUploadTemplate[product]}`
    : ''
}

export const fileHeaders = {
  crisprgrna:
    'Enter Up To 100 Gene Symbols, Gene IDs, or MISSION™ gRNA IDs Below (1 per cell)',
  shrna:
    'Enter Up To 100 Gene Symbols, Gene IDs, or Clone IDs Below (1 per cell)',
  orf: 'Enter Up To 100 Gene Symbols, Gene IDs, Ref Seqs, or Clone IDs Below (1 per cell)',
  crispr:
    'Enter Up To 100 Gene Symbols, Gene IDs, or Sanger Clone IDs Below (1 per cell)',
}

export const headerPosition = {
  crisprgrna: 'A4',
  shrna: 'A3',
  orf: 'A3',
  crispr: 'A3',
}
export const SemiConfiguratorSpeciesList = {
  [PREDESIGNED_CRISPR_GRNA]: [
    {
      name: 'Human',
      label: (
        <FormattedMessage id="CONF_HUMAN_crisprgrna" defaultMessage="Human" />
      ),
    },
    {
      name: 'Mouse',
      label: (
        <FormattedMessage id="CONF_MOUSE_crisprgrna" defaultMessage="Mouse" />
      ),
    },
  ],
}

export const convertToUppercaseBasedOnCondition = (data?: string) =>
  data !== '' && data === 'n/a' ? 'N/A' : data

export const getProductBasedUniqueIdName = (product: string): string => {
  switch (product) {
    case SANGERCRISPR:
      return 'sangerCloneId'
    case ORF:
      return 'cloneId'
    case PREDESIGNED_SIRNA:
      return 'refseqId'
    case PREDESIGNED_CRISPR_GRNA:
      return 'grnaId'
    case PREDESIGNED_SHRNA:
      return 'trcId'
    default:
      return ''
  }
}

export enum PhysicalMaterialTypes {
  RNA = 'RNA',
  VIRUS = 'VIRUS',
  DNA = 'DNA',
}
